import Alpine from 'alpinejs';
import collapse from '@alpinejs/collapse';
import { DateTime, Duration } from "luxon";
import { easepick, RangePlugin } from '@easepick/bundle';

window.Alpine = Alpine;
Alpine.plugin(collapse);

let pickerArgs = {
	format: 'D MMM YYYY',
	element: document.getElementById('datepicker'),
	css: [
		`${ window.location.origin }/app/themes/lincoln-arts/assets/dist/css/easepick.css`
	],
	plugins: [RangePlugin],
	RangePlugin: {
		tooltip: false,
		delimiter: ' to '
	}
};

document.addEventListener("alpine:init", () => {

	Alpine.data('filtering', () => ({

		endpoint: '/wp-json/lincoln/v1/events',
		params: null,
		isLoading: true,
		isFiltering: false,
		isPaginating: false,
		dateFormat: 'YYYY-MM-DD',
		results: [],
		layoutMode: 'alt',
		picker: null,
		queryArgs: {
			event_type: null,
			theme: null,
			date_filter: null,
			start_date: null,
			end_date: null,
			search: null
		},
		queryVars: [],
		paged: 1,
		postsPerPage: 10,
		foundPosts: null,
		maxNumPages: null,
		init() {

			this.initialState();

			// init posts_per_page depending upon layoutMode
			this.setPagination();

			// add initial values to picker
			let newPickerArgs = pickerArgs;
			if( this.queryArgs.start_date  ){
				newPickerArgs.RangePlugin.startDate = DateTime.fromISO( this.queryArgs.start_date ).toMillis();
			}
			if( this.queryArgs.end_date  ){
				newPickerArgs.RangePlugin.endDate = DateTime.fromISO( this.queryArgs.end_date ).toMillis();
			}

			// setup datepicker
			this.picker = new easepick.create( newPickerArgs );
			this.picker.on('select', (e) => {
				const { start, end } = e.detail;
				let q = this.queryArgs;
				q.date_filter = 'date-range';
				q.start_date = start.format( this.dateFormat );
				q.end_date = end.format( this.dateFormat );
			});

			// load initial posts
			this.fetchItems().then( (data) => {
				this.results = data;
				this.isLoading = false;
			});

			// watch for any filter changes
			this.$watch('queryArgs', () => {
				this.isFiltering = true;
				this.paged = 1; // always set to first page again
				this.setState();
				this.setPickerRange();
				this.fetchItems().then( (data) => {
					this.results = data;
					this.isFiltering = false;
				});
			});

		},

		initialState() {
			const params = new URLSearchParams(location.search);
			
			for (const key of params.keys()) {
				if ( key in this.queryArgs ){
					this.queryArgs[key] = params.get(key);
					if( params.get(key) == 'date_filter'){
						switch( params.get('date_filter') ){
							case 'today':
								this.filterToday();
								break;
							case 'week':
								this.filterWeek();
								break;
							case 'month':
								this.filterMonth();
								break;
							case 'date-range':

							break;
						}
					}
					
				}
			}

		},

		setState(){
			const url = new URL(window.location.href.split('?')[0]);

			Object.keys( this.queryArgs ).forEach(key => {
				if( this.queryArgs[key] !== null ){
					url.searchParams.set(key, this.queryArgs[key]);
				}
			});
			
			history.pushState(null, document.title, url.toString());
		},

		async fetchItems () {
			this.doParams();
			const endpoint = this.params ? this.endpoint + this.params : this.endpoint;
			const dataPromise = await fetch( endpoint )
				.then((res) => res.json())
				.then((data) => {
				return data;
			});
			const data = await Promise.resolve(dataPromise);
			this.foundPosts = data.found_posts;
			this.maxNumPages = data.max_num_pages;
			return Array.from(data.items);
		},

		loadNextPage() {
			this.paged++;
			this.isPaginating = true;
			this.fetchItems().then( (data) => {
				let currentResults = this.results;
				this.results = currentResults.concat( data );
				this.isPaginating = false;
			});
		},

		doParams() {

			let q = this.queryArgs;
			let params = [];

			if( q.start_date ){
				params.push( `start_date=${ q.start_date }` );
			}

			if( q.end_date ){
				params.push( `end_date=${ q.end_date }` );
			}

			if( q.search && q.search !== "" ){
				params.push( `s=${ q.search }` );
			}

			if( q.event_type ){
				params.push( `event_type=${ q.event_type }` );
			}

			if( q.theme ){
				params.push( `theme=${ q.theme }` );
			}

			if( this.paged ){
				params.push( `paged=${ this.paged }` );
			}

			if( this.postsPerPage ){
				params.push( `posts_per_page=${ this.postsPerPage }` );
			}

			if( params.length ){
				this.params = `?${ params.join('&') }`;
			}else{
				this.params = null;
			}

		},

		buildQueryArgs(){
			const searchParams = new URLSearchParams(location.search);
			for (const p of searchParams) {
				this.queryVars.push(p);
			}
		},

		doTextSearch( val ){
			this.queryArgs.search = !val || val.length === 0 ? null : val;
		},

		setPagination() {
			this.postsPerPage = this.layoutMode == "alt" ? 10 : 12;
		},

		isAltItem( i ) {
			return ((i + 1) % 5 === 1 % 5 || (i + 1) % 5 === 2 % 5 || (i + 1) % 5 === 6 % 5 || (i + 1) % 5 === 7 % 5);
		},

		clearFilter() {
			this.queryArgs = {
				event_type: null,
				theme: null,
				date_filter: null,
				start_date: null,
				end_date: null,
				s: null
			};
			this.params = null;
			this.paged = 1;
			this.postsPerPage = this.setPagination();
		},

		clearDates() {
			this.queryArgs.date_filter = null;
			this.queryArgs.start_date = null;
			this.queryArgs.end_date = null;
		},

		filterToday() {
			this.queryArgs.start_date = DateTime.now().toISODate();
			this.queryArgs.end_date = DateTime.now().toISODate();
			this.queryArgs.date_filter = 'today';
		},

		filterWeek() {
			this.queryArgs.start_date = DateTime.now().toISODate();
			this.queryArgs.end_date = DateTime.now().plus(  Duration.fromObject({ days: 7 }) ).toISODate();
			this.queryArgs.date_filter = 'week';
		},

		filterMonth() {
			this.queryArgs.start_date = DateTime.now().toISODate();
			this.queryArgs.end_date = DateTime.local().endOf('month').toISODate();
			this.queryArgs.date_filter = 'month';
		},

		setPickerRange() {
			if( this.queryArgs.start_date == null && this.queryArgs.end_date == null ){
				this.picker.clear();
			}else{
				this.picker.setStartDate( DateTime.fromISO( this.queryArgs.start_date ).toMillis() );
				this.picker.setEndDate( DateTime.fromISO( this.queryArgs.end_date ).toMillis() );
			}
			
		}
	}));

});


window.onload = function(){

	const sfilter = document.querySelector('.static-filter');
	if( sfilter ){
		const pickerMethods = {
			setup(picker) {
				picker.on('select', (e) => {
					const start = e.detail.start.format( 'YYYY-MM-DD' );
					const end = e.detail.end.format( 'YYYY-MM-DD' );

					const url = new URL( sfilter.dataset.baseurl, window.location.href.split('?')[0] );
					url.searchParams.set('start_date', start);
					url.searchParams.set('end_date', end);
					
					if( start == end ){
						url.searchParams.set('date_filter', 'today');
					}else{
						url.searchParams.set('date_filter', 'date-range');
					}

					window.location.href = url.toString() + '#filter';

				});
			}
		};

		const newPickerArgs = Object.assign({}, pickerArgs, pickerMethods);
		const picker = new easepick.create(newPickerArgs);
	}

}